import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Bell, Info, Play, Filter, Plus, Trash2, AlertTriangle, Users, Calendar, MousePointer, Eye } from 'lucide-react';
import SignalChart from '../components/SignalChart';
const ActionDetail: React.FC = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'actions' | 'correlations'>('actions');
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [highlightedElements, setHighlightedElements] = useState<string[]>([]);
  // Mock action data
  const action = {
    id: parseInt(id || '1'),
    name: 'Created New Assembly',
    type: 'User',
    description: 'User clicked create assembly button',
    occurrences: 47,
    lastTriggered: '2 hours ago',
    alert: 'None',
    status: 'Active',
    createdAt: 'Jan 15, 2023',
    owner: 'John Smith',
    affectedUsers: 28,
    affectedAccounts: 12,
    correlationScore: 87
  };
  // Updated chart data with more realistic time series
  const actionChartData = {
    id: parseInt(id || '1'),
    title: 'Created New Assembly',
    type: 'bar',
    subtitle: 'Occurrences Over Time',
    actionType: 'User Action',
    timeSeriesData: [{
      date: 'Oct 1',
      value: 2
    }, {
      date: 'Oct 2',
      value: 3
    }, {
      date: 'Oct 3',
      value: 5
    }, {
      date: 'Oct 4',
      value: 4
    }, {
      date: 'Oct 5',
      value: 7
    }, {
      date: 'Oct 6',
      value: 8
    }, {
      date: 'Oct 7',
      value: 6
    }, {
      date: 'Oct 8',
      value: 9
    }, {
      date: 'Oct 9',
      value: 11
    }, {
      date: 'Oct 10',
      value: 8
    }, {
      date: 'Oct 11',
      value: 7
    }, {
      date: 'Oct 12',
      value: 10
    }, {
      date: 'Oct 13',
      value: 12
    }, {
      date: 'Oct 14',
      value: 9
    }],
    trend: 'up',
    percentage: 23,
    timeframe: 'vs previous period'
  };
  // Mock data for user action filters
  const actionFilters = [{
    id: 1,
    type: 'click',
    element: 'Create Assembly Button',
    selector: '#create-assembly-btn',
    count: 47,
    correlation: 'high'
  }, {
    id: 2,
    type: 'view',
    element: 'Assembly Dashboard',
    selector: '.dashboard-view',
    count: 68,
    correlation: 'medium'
  }, {
    id: 3,
    type: 'input',
    element: 'Assembly Name Field',
    selector: '#assembly-name-input',
    count: 42,
    correlation: 'high'
  }, {
    id: 4,
    type: 'select',
    element: 'Assembly Type Dropdown',
    selector: '#assembly-type-select',
    count: 38,
    correlation: 'high'
  }, {
    id: 5,
    type: 'click',
    element: 'Add Team Member Button',
    selector: '.add-team-btn',
    count: 29,
    correlation: 'medium'
  }];
  // Mock data for affected users
  const affectedUsers = [{
    id: 1,
    name: 'Sarah Johnson',
    email: 'sarah.j@acme.com',
    account: 'Acme Corporation',
    occurrences: 5,
    lastTriggered: '1 day ago'
  }, {
    id: 2,
    name: 'Michael Brown',
    email: 'm.brown@stark.com',
    account: 'Stark Industries',
    occurrences: 3,
    lastTriggered: '3 hours ago'
  }, {
    id: 3,
    name: 'Emily Davis',
    email: 'e.davis@globex.com',
    account: 'Globex Corporation',
    occurrences: 4,
    lastTriggered: '5 hours ago'
  }];
  const handleElementClick = (filterId: number) => {
    const filter = actionFilters.find(f => f.id === filterId);
    if (filter) {
      setSelectedElement(filter.element);
      // Simulate highlighting elements in the preview
      const relatedElements = actionFilters.filter(f => f.correlation === 'high' && f.id !== filterId).map(f => f.element);
      setHighlightedElements(relatedElements);
    }
  };
  const getCorrelationColor = (correlation: string) => {
    switch (correlation) {
      case 'high':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getActionIcon = (type: string) => {
    switch (type) {
      case 'click':
        return <MousePointer size={16} className="text-blue-500" data-id="element-517" />;
      case 'view':
        return <Eye size={16} className="text-purple-500" data-id="element-518" />;
      case 'input':
        return <Edit size={16} className="text-green-500" data-id="element-519" />;
      case 'select':
        return <MousePointer size={16} className="text-orange-500" data-id="element-520" />;
      default:
        return <MousePointer size={16} className="text-gray-500" data-id="element-521" />;
    }
  };
  return <div className="space-y-6" data-id="element-522">
      <div className="flex items-center gap-4" data-id="element-523">
        <button onClick={() => navigate('/actions')} className="p-2 rounded-full hover:bg-gray-100" data-id="element-524">
          <ArrowLeft size={20} data-id="element-525" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-526">{action.name}</h1>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${action.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-527">
          {action.status}
        </span>
        <button className="ml-auto bg-[#0070d2] hover:bg-[#0057a3] text-white px-4 py-2 rounded-md flex items-center gap-2" data-id="element-528">
          <Edit size={16} data-id="element-529" />
          <span data-id="element-530">Edit Action</span>
        </button>
      </div>
      <div className="grid grid-cols-3 gap-6" data-id="element-531">
        {/* Action stats cards */}
        <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm" data-id="element-532">
          <div className="flex items-center gap-3" data-id="element-533">
            <div className="p-2 rounded-full bg-blue-100 text-blue-600" data-id="element-534">
              <Bell size={20} data-id="element-535" />
            </div>
            <div data-id="element-536">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-537">Occurrences</h3>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-538">
                {action.occurrences}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm" data-id="element-539">
          <div className="flex items-center gap-3" data-id="element-540">
            <div className="p-2 rounded-full bg-green-100 text-green-600" data-id="element-541">
              <Users size={20} data-id="element-542" />
            </div>
            <div data-id="element-543">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-544">
                Affected Users
              </h3>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-545">
                {action.affectedUsers}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm" data-id="element-546">
          <div className="flex items-center gap-3" data-id="element-547">
            <div className="p-2 rounded-full bg-purple-100 text-purple-600" data-id="element-548">
              <Calendar size={20} data-id="element-549" />
            </div>
            <div data-id="element-550">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-551">
                Last Triggered
              </h3>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-552">
                {action.lastTriggered}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Main content tabs */}
      <div className="bg-white border border-gray-200 rounded-md shadow-sm" data-id="element-553">
        <div className="border-b border-gray-200" data-id="element-554">
          <nav className="flex -mb-px" data-id="element-555">
            <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'actions' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('actions')} data-id="element-556">
              User Actions
            </button>
            <button className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'correlations' ? 'border-[#0070d2] text-[#0070d2]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('correlations')} data-id="element-557">
              Correlations
            </button>
          </nav>
        </div>
        <div className="p-6" data-id="element-558">
          {activeTab === 'actions' && <div className="grid grid-cols-3 gap-6" data-id="element-559">
              <div className="col-span-1" data-id="element-560">
                <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4" data-id="element-561">
                  <div className="flex items-center justify-between mb-3" data-id="element-562">
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-563">
                      User Action Filters
                    </h3>
                    <button className="text-xs text-[#0070d2] flex items-center gap-1" data-id="element-564">
                      <Plus size={14} data-id="element-565" />
                      <span data-id="element-566">Add Filter</span>
                    </button>
                  </div>
                  <div className="space-y-2" data-id="element-567">
                    {actionFilters.map(filter => <div key={filter.id} className={`p-3 border ${selectedElement === filter.element ? 'border-blue-500 bg-blue-50' : 'border-gray-200'} rounded-md hover:bg-gray-50 cursor-pointer`} onClick={() => handleElementClick(filter.id)} data-id="element-568">
                        <div className="flex items-start" data-id="element-569">
                          <div className="mr-2 mt-0.5" data-id="element-570">
                            {getActionIcon(filter.type)}
                          </div>
                          <div className="flex-1" data-id="element-571">
                            <div className="flex items-center justify-between" data-id="element-572">
                              <h4 className="text-sm font-medium text-gray-900" data-id="element-573">
                                {filter.element}
                              </h4>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getCorrelationColor(filter.correlation)}`} data-id="element-574">
                                {filter.correlation}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1" data-id="element-575">
                              {filter.type} action • {filter.count} occurrences
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono" data-id="element-576">
                              {filter.selector}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
              <div className="col-span-2" data-id="element-577">
                <div className="bg-white border border-gray-200 rounded-md shadow-sm" data-id="element-578">
                  <div className="border-b border-gray-200 px-4 py-3" data-id="element-579">
                    <div className="flex justify-between items-center" data-id="element-580">
                      <h3 className="text-sm font-medium text-gray-900" data-id="element-581">
                        Browser Preview
                      </h3>
                      <div className="text-xs text-gray-500" data-id="element-582">
                        May 10, 2023 • 10:30 AM
                      </div>
                    </div>
                  </div>
                  <div className="p-4" data-id="element-583">
                    <div className="border border-gray-200 rounded-md overflow-hidden" data-id="element-584">
                      {/* Browser chrome */}
                      <div className="bg-gray-100 px-3 py-2 flex items-center gap-2 border-b border-gray-200" data-id="element-585">
                        <div className="flex gap-1.5" data-id="element-586">
                          <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-587"></div>
                          <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-588"></div>
                          <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-589"></div>
                        </div>
                        <div className="flex-1 bg-white text-xs text-center py-1 rounded-md text-gray-600 truncate" data-id="element-590">
                          https://salesforce.com/lightning/opportunity
                        </div>
                      </div>
                      {/* Browser content area - replaced with Distru web portal image */}
                      <div className="relative bg-white" style={{
                    height: '500px'
                  }} data-id="element-591">
                        {selectedElement ? <div className="w-full h-full" data-id="element-592">
                            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rxyErhw1SF7i5F6WP41ksR/Salesforce2BLightning-2791053328.png" alt="Salesforce Lightning Interface" className="w-full h-full object-cover" data-id="element-593" />
                            {/* Tooltip overlay */}
                            <div className="absolute top-2 right-2 bg-white border border-gray-200 rounded-md shadow-md p-3 max-w-xs" data-id="element-594">
                              <h4 className="text-sm font-medium text-gray-900 mb-1" data-id="element-595">
                                {selectedElement}
                              </h4>
                              <p className="text-xs text-gray-500 mb-2" data-id="element-596">
                                This element is highly correlated with the "
                                {action.name}" action.
                              </p>
                              <div className="flex items-center text-xs text-gray-700" data-id="element-597">
                                <span className="font-medium mr-1" data-id="element-598">
                                  Correlation:
                                </span>
                                <span className="text-green-600 font-medium" data-id="element-599">
                                  High
                                </span>
                              </div>
                            </div>
                          </div> : <div className="flex flex-col items-center justify-center h-full text-center p-4" data-id="element-600">
                            <p className="text-sm text-gray-600 mb-2" data-id="element-601">
                              Select an action filter to see the browser preview
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-602">
                              The preview shows what the user would see when
                              performing this action
                            </p>
                          </div>}
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-gray-500" data-id="element-603">
                      <p data-id="element-604">
                        Select an action filter on the left to highlight the
                        corresponding element in the preview.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          {activeTab === 'correlations' && <div className="grid grid-cols-2 gap-6" data-id="element-605">
              <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4" data-id="element-606">
                <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-607">
                  Related Actions
                </h3>
                <div className="space-y-3" data-id="element-608">
                  <div className="p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer" data-id="element-609">
                    <div className="flex justify-between" data-id="element-610">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-611">
                        Completed Assembly Setup
                      </h4>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800" data-id="element-612">
                        high
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-613">
                      User completed all assembly setup steps
                    </p>
                    <div className="flex items-center mt-2" data-id="element-614">
                      <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-615">
                        <div className="bg-green-500 h-1.5 rounded-full" style={{
                      width: '78%'
                    }} data-id="element-616"></div>
                      </div>
                      <span className="ml-2 text-xs text-gray-600" data-id="element-617">78%</span>
                    </div>
                  </div>
                  <div className="p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer" data-id="element-618">
                    <div className="flex justify-between" data-id="element-619">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-620">
                        Invited Team Member
                      </h4>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800" data-id="element-621">
                        medium
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-622">
                      User invited a new team member
                    </p>
                    <div className="flex items-center mt-2" data-id="element-623">
                      <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-624">
                        <div className="bg-yellow-500 h-1.5 rounded-full" style={{
                      width: '62%'
                    }} data-id="element-625"></div>
                      </div>
                      <span className="ml-2 text-xs text-gray-600" data-id="element-626">62%</span>
                    </div>
                  </div>
                  <div className="p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer" data-id="element-627">
                    <div className="flex justify-between" data-id="element-628">
                      <h4 className="text-sm font-medium text-gray-900" data-id="element-629">
                        Dashboard View
                      </h4>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-800" data-id="element-630">
                        low
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-631">
                      User viewed main dashboard
                    </p>
                    <div className="flex items-center mt-2" data-id="element-632">
                      <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-633">
                        <div className="bg-gray-500 h-1.5 rounded-full" style={{
                      width: '45%'
                    }} data-id="element-634"></div>
                      </div>
                      <span className="ml-2 text-xs text-gray-600" data-id="element-635">45%</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-md shadow-sm p-4" data-id="element-636">
                <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-637">
                  User Flow Analysis
                </h3>
                <p className="text-xs text-gray-500 mb-3" data-id="element-638">
                  Common user paths before and after this action was triggered.
                </p>
                <div className="space-y-4" data-id="element-639">
                  <div data-id="element-640">
                    <h4 className="text-xs font-medium text-gray-700 mb-2" data-id="element-641">
                      Before Action
                    </h4>
                    <div className="relative pb-6" data-id="element-642">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-643"></div>
                      <div className="relative flex items-start mb-4" data-id="element-644">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 z-10 mr-3" data-id="element-645">
                          <span className="text-xs font-medium" data-id="element-646">1</span>
                        </div>
                        <div className="bg-gray-50 rounded-md p-2 flex-1" data-id="element-647">
                          <h5 className="text-sm font-medium text-gray-900" data-id="element-648">
                            Login
                          </h5>
                          <p className="text-xs text-gray-500" data-id="element-649">
                            User logged into account
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-650">
                            85% of users
                          </p>
                        </div>
                      </div>
                      <div className="relative flex items-start mb-4" data-id="element-651">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 z-10 mr-3" data-id="element-652">
                          <span className="text-xs font-medium" data-id="element-653">2</span>
                        </div>
                        <div className="bg-gray-50 rounded-md p-2 flex-1" data-id="element-654">
                          <h5 className="text-sm font-medium text-gray-900" data-id="element-655">
                            Dashboard View
                          </h5>
                          <p className="text-xs text-gray-500" data-id="element-656">
                            User viewed main dashboard
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-657">
                            72% of users
                          </p>
                        </div>
                      </div>
                      <div className="relative flex items-start" data-id="element-658">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 z-10 mr-3" data-id="element-659">
                          <span className="text-xs font-medium" data-id="element-660">3</span>
                        </div>
                        <div className="bg-gray-50 rounded-md p-2 flex-1" data-id="element-661">
                          <h5 className="text-sm font-medium text-gray-900" data-id="element-662">
                            Assembly List
                          </h5>
                          <p className="text-xs text-gray-500" data-id="element-663">
                            User viewed list of assemblies
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-664">
                            68% of users
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-665">
                    <h4 className="text-xs font-medium text-gray-700 mb-2" data-id="element-666">
                      After Action
                    </h4>
                    <div className="relative pb-2" data-id="element-667">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-668"></div>
                      <div className="relative flex items-start mb-4" data-id="element-669">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600 z-10 mr-3" data-id="element-670">
                          <span className="text-xs font-medium" data-id="element-671">1</span>
                        </div>
                        <div className="bg-gray-50 rounded-md p-2 flex-1" data-id="element-672">
                          <h5 className="text-sm font-medium text-gray-900" data-id="element-673">
                            Assembly Setup
                          </h5>
                          <p className="text-xs text-gray-500" data-id="element-674">
                            User configured assembly settings
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-675">
                            92% of users
                          </p>
                        </div>
                      </div>
                      <div className="relative flex items-start" data-id="element-676">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 text-green-600 z-10 mr-3" data-id="element-677">
                          <span className="text-xs font-medium" data-id="element-678">2</span>
                        </div>
                        <div className="bg-gray-50 rounded-md p-2 flex-1" data-id="element-679">
                          <h5 className="text-sm font-medium text-gray-900" data-id="element-680">
                            Add Team Member
                          </h5>
                          <p className="text-xs text-gray-500" data-id="element-681">
                            User invited team members
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-682">
                            65% of users
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};
export default ActionDetail;